import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Check, X, MessageCircle, Send } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function TicketView() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`)
      .then((r) => setBooking(r.data))
      .catch(() => setErr(true));
  }, [id]);

  const isAdmin = !!(user && user !== false);

  const buildMessage = () => {
    if (!booking) return "";
    const code = booking.id.slice(0, 8).toUpperCase();
    const currency = booking.currency || "USD";
    const subtotal = Number(booking.total_amount || 0).toFixed(2);
    const final = Number(booking.final_amount ?? booking.total_amount ?? 0).toFixed(2);
    const hasDiscount = booking.coupon_code && Number(booking.final_amount ?? booking.total_amount) < Number(booking.total_amount);
    const lines = [
      `🎟️ *TICKET BENOVA TRAVEL*`,
      ``,
      `*${booking.experience_name}*`,
      ``,
      `📌 *Reserva:* ${code}`,
      `👤 *Titular:* ${booking.customer_name}`,
      `📅 *Fecha:* ${booking.booking_date}`,
      `👥 *Personas:* ${booking.num_people}`,
    ];
    if (booking.customer_email) lines.push(`✉️ *Email:* ${booking.customer_email}`);
    if (booking.customer_phone) lines.push(`📱 *Tel:* ${booking.customer_phone}`);
    lines.push(``);
    if (hasDiscount) {
      lines.push(`💰 *Subtotal:* $${subtotal} ${currency}`);
      lines.push(`🏷️ *Cupón:* ${booking.coupon_code}`);
      lines.push(`✅ *Descuento:* -$${(Number(subtotal) - Number(final)).toFixed(2)} ${currency}`);
      lines.push(`💳 *Total pagado:* $${final} ${currency}`);
    } else {
      lines.push(`💳 *Total pagado:* $${final} ${currency}`);
    }
    lines.push(``);
    lines.push(`🔗 Verificar: ${window.location.origin}/ticket/${booking.id}`);
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(buildMessage());
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const copyAndOpenWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(buildMessage());
      window.open("https://web.whatsapp.com/", "_blank");
    } catch (e) {
      shareWhatsApp();
    }
  };

  const currency = booking?.currency || "USD";
  const subtotal = Number(booking?.total_amount || 0);
  const finalAmount = booking?.final_amount !== undefined && booking?.final_amount !== null
    ? Number(booking.final_amount)
    : subtotal;
  const hasDiscount = booking?.coupon_code && finalAmount < subtotal;
  const discount = subtotal - finalAmount;

  return (
    <div data-testid="ticket-page">
      <Navbar />
      <section className="pt-32 pb-20 bn-section">
        <div className="bn-container px-6 md:px-8 max-w-3xl mx-auto">
          {err && <div className="text-center py-20 bn-heading text-2xl">{t("ticket.not_found")}</div>}
          {booking && (
            <>
              <div className="bn-card overflow-hidden">
                <div className="p-8" style={{ background: booking.status === "paid" ? "var(--bn-accent)" : "#b91c1c", color: "white" }}>
                  <div className="flex items-center gap-3">
                    {booking.status === "paid" ? <Check size={22} /> : <X size={22} />}
                    <span className="bn-eyebrow">{booking.status === "paid" ? t("ticket.valid") : t("ticket.invalid")}</span>
                  </div>
                  <h1 className="mt-4 text-3xl md:text-5xl" style={{ fontFamily: "var(--bn-font-heading)" }}>{booking.experience_name}</h1>
                </div>
                <div className="p-8 grid md:grid-cols-5 gap-8">
                  <div className="md:col-span-3 space-y-4">
                    <Row label={t("ticket.booking")} value={booking.id.slice(0, 8).toUpperCase()} mono />
                    <Row label={t("pay.holder")} value={booking.customer_name} />
                    <Row label={t("detail.date")} value={booking.booking_date} />
                    <Row label={t("detail.people")} value={String(booking.num_people)} />
                    {hasDiscount && (
                      <>
                        <div style={{ borderTop: "1px dashed var(--bn-border)", margin: "10px 0" }} />
                        <Row label="Subtotal" value={`$${subtotal.toFixed(2)} ${currency}`} muted />
                        <Row label="Cupón" value={booking.coupon_code} accent mono />
                        <Row label="Descuento" value={`- $${discount.toFixed(2)} ${currency}`} accent />
                      </>
                    )}
                    <div style={{ borderTop: hasDiscount ? "1px solid var(--bn-border)" : "none", paddingTop: hasDiscount ? "12px" : "0" }}>
                      <Row label={hasDiscount ? "Total pagado" : t("detail.total")} value={`$${finalAmount.toFixed(2)} ${currency}`} big />
                    </div>
                  </div>
                  <div className="md:col-span-2 flex items-center justify-center">
                    <div className="p-3 bg-white rounded">
                      <QRCodeCanvas value={`${window.location.origin}/ticket/${booking.id}`} size={180} level="H" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Share actions (visible to admins; also available to the customer who paid) */}
              <div className="flex flex-wrap gap-3 justify-center mt-8 print:hidden" data-testid="ticket-share-bar">
                <button onClick={shareWhatsApp} className="bn-btn-accent inline-flex items-center gap-2" data-testid="ticket-share-whatsapp">
                  <MessageCircle size={16} /> Compartir por WhatsApp
                </button>
                {isAdmin && (
                  <button onClick={copyAndOpenWhatsApp} className="bn-btn-ghost inline-flex items-center gap-2" data-testid="ticket-copy-whatsapp">
                    <Send size={16} /> Copiar y abrir WhatsApp Web
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({ label, value, big, accent, muted, mono }) {
  const valueStyle = {
    fontFamily: mono ? "var(--bn-font-heading)" : undefined,
    letterSpacing: mono ? "0.1em" : undefined,
    color: accent ? "var(--bn-accent)" : muted ? "var(--bn-text-muted)" : "var(--bn-text)",
    fontSize: big ? "1.5rem" : undefined,
    fontWeight: big ? 600 : undefined,
  };
  return (
    <div className="flex justify-between gap-3 items-baseline">
      <span className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>{label}</span>
      <span className={big ? "bn-heading" : "bn-body-font"} style={valueStyle}>{value}</span>
    </div>
  );
}
